<template>
  <x-form
    :title="$t('Attivazione autenticazione a due fattori')"
    variant="small"
    modal
    open
    @update:open="$emit('update:open', $event)"
    @cancel="$emit('update:enabled', false)"
    @submit="verify"
  >
    <pf-spinner v-if="!totp" />

    <template v-else>
      <pf-flex direction="column" align-items="center">
        <img :src="`/qrcode/${totp.uri}.png`" alt="QR code" width="300" />
        {{ $t('Scansiona il codice QR con app di autenticazione') }}
      </pf-flex>

      <form-group :name="$t('Oppure copia il codice manualmente')">
        <x-input id="anagraficaTwoFA" readonly type="text" v-model="totp.secret" @click="secretClick" />
      </form-group>

      <form-group :name="$t('Inserisci il codice generato dall\'app')">
        <x-input id="twoFAVerifyCode" v-model="verifyCode" required />
      </form-group>
    </template>
  </x-form>
</template>

<style scoped>
img {
  width: 300px;
}
</style>

<script lang="ts" setup>
import { http } from '@/http';
import { $t as _t } from '@/i18n';
import { asyncComputed } from '@common/asyncComputed';
import { copyToClipBoard } from '@common/utils';
import { ref } from 'vue';
import { useAlertStore } from '@/store/alerts';
import type { ComponentProps } from 'vue-component-type-helpers';
import type XForm from './x-form.vue';

type XFormProps = ComponentProps<typeof XForm>;

interface Props extends /* @vue-ignore */ Omit<XFormProps, 'variant' | 'title'> {
  enabled?: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'update:enabled', value: boolean): void;
}>();

const { result: totp } = asyncComputed(async () => {
  const totp = (await http.get<{ uri: string; secret: string }>('/api/auth/totp')).data;
  totp.uri = btoa(totp.uri);
  return totp;
}, null);

const verifyCode = ref('');

function secretClick(e: Event) {
  if (!(e.target instanceof HTMLInputElement)) {
    return;
  }
  e.target.select();
  copyToClipBoard(e.target.value);

  const alertStore = useAlertStore();
  alertStore.add({ title: _t('Codice copiato nella clipboard'), type: 'info', toast: true });
}

async function verify() {
  const data = new FormData();
  data.append('totp', verifyCode.value);
  const response = await http.post('/api/auth/totp', data);
  if (response.data.level === 'success') {
    emit('update:open', false);
    emit('update:enabled', true);
  }
}
</script>
